import { useState, useRef, useEffect } from "react";
import { usePageTitle } from "../hooks/usePageTitle";
import TestimonialSection from "../components/HomepageSection/TestimonialSection";
import Footer from "../components/Footer";

// ─── Helpers ───────────────────────────────────────────────────────────────
const POSTS_PER_PAGE = 4;

function getInitials(name) {
  return name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
}

function applyFilters(articles, category, tag, sort, search) {
  let result = [...articles];
  if (category !== "All categories") result = result.filter((a) => a.category === category);
  if (tag !== "All tags") result = result.filter((a) => a.tags.includes(tag));
  if (search.trim()) {
    const q = search.toLowerCase();
    result = result.filter((a) =>
      a.title.toLowerCase().includes(q) ||
      a.excerpt.toLowerCase().includes(q) ||
      a.author.toLowerCase().includes(q) ||
      a.tags.some((t) => t.toLowerCase().includes(q))
    );
  }
  if (sort === "Newest") result.sort((a, b) => new Date(b.date) - new Date(a.date));
  else if (sort === "Oldest") result.sort((a, b) => new Date(a.date) - new Date(b.date));
  else if (sort === "Popular") result.sort((a, b) => b.popularity - a.popularity);
  else if (sort === "A-Z") result.sort((a, b) => a.title.localeCompare(b.title));
  return result;
}

// ─── Carousel Card ──────────────────────────────────────────────────────────
function CarouselCard({ article, onNavigate }) {
  return (
    <div
      className="relative flex-shrink-0 w-72 h-52 rounded-2xl overflow-hidden cursor-pointer group"
      onClick={() => onNavigate(article.slug)}
    >
      <img src={article.image} alt={article.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-black-deep/85 via-black-deep/30 to-transparent" />
      <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white-pure flex items-center justify-center opacity-90 group-hover:scale-110 transition-transform duration-200">
        <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
          <path d="M2 12L12 2M12 2H5M12 2v7" stroke="#012E72" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <div className="absolute bottom-0 left-0 right-0 p-4">
        <p className="font-cinzel font-bold text-white-pure/70 text-[0.58rem] tracking-[0.2em] uppercase mb-1">{article.category}</p>
        <h3 className="font-cinzel font-bold text-white-pure text-[0.8rem] leading-snug line-clamp-2">{article.title}</h3>
      </div>
    </div>
  );
}

// ─── Blog Post Card ──────────────────────────────────────────────────────────
function BlogCard({ article, onNavigate }) {
  return (
    <div
      className="bg-white-pure border border-blue-dark/10 rounded-2xl overflow-hidden shadow-[0_2px_16px_rgba(1,46,114,0.07)] hover:shadow-[0_6px_32px_rgba(1,46,114,0.13)] hover:-translate-y-1 transition-all duration-300 cursor-pointer flex flex-col"
      onClick={() => onNavigate(article.slug)}
    >
      <div className="h-52 overflow-hidden">
        <img src={article.image} alt={article.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
      </div>
      <div className="p-6 flex flex-col flex-1 gap-3">
        <div className="flex items-center gap-3">
          <span className="font-constantia text-xs text-blue-dark/50">{article.date}</span>
          <span className="font-cinzel font-bold text-[0.6rem] tracking-[0.15em] uppercase text-blue-primary border border-blue-primary/40 rounded-full px-2.5 py-0.5">
            {article.category}
          </span>
        </div>
        <h3 className="font-cinzel font-bold text-blue-dark text-base leading-snug italic line-clamp-2 hover:text-blue-primary transition-colors duration-200">
          {article.title}
        </h3>
        <p className="font-constantia text-sm text-blue-dark/65 leading-relaxed line-clamp-2 flex-1">
          {article.excerpt}
        </p>
        <div className="flex flex-wrap gap-1.5">
          {article.tags.map((tag) => (
            <span key={tag} className="font-cinzel font-bold text-[0.58rem] tracking-[0.12em] uppercase text-blue-dark/60 border border-blue-dark/20 rounded-full px-2.5 py-0.5">
              {tag}
            </span>
          ))}
        </div>
        <div className="flex items-center gap-3 pt-3 border-t border-blue-dark/8">
          <div className="w-9 h-9 rounded-full bg-blue-dark flex items-center justify-center flex-shrink-0">
            <span className="font-cinzel font-bold text-[0.6rem] text-white-pure tracking-wide">{getInitials(article.author)}</span>
          </div>
          <div>
            <p className="font-cinzel font-bold text-blue-dark text-[0.72rem]">{article.author}</p>
            <p className="font-constantia text-blue-dark/50 text-[0.72rem]">{article.role}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────
export default function BlogAndArticlesPage({ onNavigate }) {
  usePageTitle("Blog & Articles");
  const [articles,  setArticles]  = useState([]);
  const [loadingDB, setLoadingDB] = useState(true);
  const [category,  setCategory]  = useState("All categories");
  const [tag,       setTag]       = useState("All tags");
  const [sort,      setSort]      = useState("Newest");
  const [search,    setSearch]    = useState("");
  const [page,      setPage]      = useState(1);
  const carouselRef = useRef(null);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/api/blogs`)
      .then((r) => r.json())
      .then((json) => { if (json.success) setArticles(json.data); })
      .catch(() => {})
      .finally(() => setLoadingDB(false));
  }, []);

  const allCategories = ["All categories", ...Array.from(new Set(articles.map((a) => a.category)))];
  const allTags       = ["All tags",       ...Array.from(new Set(articles.flatMap((a) => a.tags)))];

  const filtered   = applyFilters(articles, category, tag, sort, search);
  const totalPages = Math.ceil(filtered.length / POSTS_PER_PAGE) || 1;
  const paginated  = filtered.slice((page - 1) * POSTS_PER_PAGE, page * POSTS_PER_PAGE);

  useEffect(() => { setPage(1); }, [category, tag, sort, search]);

  const scrollCarousel = (dir) => {
    if (carouselRef.current) carouselRef.current.scrollBy({ left: dir * 300, behavior: "smooth" });
  };

  const handleReset = () => { setCategory("All categories"); setTag("All tags"); setSort("Newest"); setSearch(""); setPage(1); };

  const selectClass = "font-cinzel text-[0.72rem] tracking-wide text-blue-dark bg-white-pure border border-blue-dark/20 rounded-full px-4 py-2.5 pr-8 appearance-none cursor-pointer focus:outline-none focus:border-blue-primary transition-colors duration-200";

  return (
    <div className="min-h-screen bg-white-warm">

      {/* ══ HERO BANNER — constrained to same max-w-7xl as carousel & grid ══ */}
      <div className="max-w-7xl mx-auto px-6 sm:px-10 pt-8">
        <div className="bg-blue-dark relative overflow-hidden rounded-3xl">

          {/* Grain overlay */}
          <div className="absolute inset-0 pointer-events-none opacity-[0.035]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
              backgroundSize: "200px 200px",
            }} />

          {/* Grid lines */}
          <div className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage: "linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)",
              backgroundSize: "80px 80px",
            }} />

          {/* Radial glow left */}
          <div className="absolute inset-0 pointer-events-none"
            style={{ background: "radial-gradient(ellipse 55% 100% at 0% 50%, rgba(0,45,181,0.45) 0%, transparent 65%)" }} />

          {/* Radial glow top */}
          <div className="absolute inset-0 pointer-events-none"
            style={{ background: "radial-gradient(ellipse 80% 40% at 50% -10%, rgba(0,45,181,0.35) 0%, transparent 70%)" }} />

          <div className="relative z-10 px-10 py-12 flex flex-col lg:flex-row items-center gap-10">

            {/* Left */}
            <div className="flex-1 space-y-5">

              {/* Badge */}
              <div className="flex items-center gap-2">
                <div className="w-6 h-px bg-white-pure/35" />
                <span className="font-cinzel font-bold text-white-pure/55 border border-white-pure/20 rounded-full px-4 py-1 text-[0.6rem] tracking-[0.3em] uppercase">
                  Insights &amp; Resources
                </span>
                <div className="w-6 h-px bg-white-pure/35" />
              </div>

              {/* Headline */}
              <h1
                className="font-cinzel font-black text-white-pure leading-[1.1] tracking-[0.03em]"
                style={{ fontSize: "clamp(1.5rem, 3vw, 2.4rem)", textShadow: "0 2px 40px rgba(0,45,181,0.5)" }}
              >
                EXPLORE OUR LATEST INSIGHTS<br />ON INSURANCE AND RISK PROTECTION.
              </h1>

              {/* Decorative rule */}
              <div className="flex items-center gap-3">
                <div className="w-12 h-px bg-gradient-to-r from-white-pure/30 to-transparent" />
                <div className="w-[5px] h-[5px] rounded-full bg-white-pure/35 border border-white-pure/50" />
              </div>

              {/* Subtitle */}
              <p className="font-constantia italic text-white-pure/55 text-[0.95rem] leading-relaxed max-w-lg">
                Browse practical guidance on insurance strategy, business protection, and risk management.
              </p>

              {/* Filters */}
              <div className="flex flex-wrap items-center gap-3 pt-1">
                {/* Search input */}
                <div className="relative">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-blue-dark/40" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search articles..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="font-cinzel text-[0.72rem] tracking-wide text-blue-dark bg-white-pure border border-blue-dark/20 rounded-full pl-8 pr-4 py-2.5 appearance-none focus:outline-none focus:border-blue-primary transition-colors duration-200 w-44"
                  />
                  {search && (
                    <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-dark/40 hover:text-blue-dark transition-colors">
                      <svg width="10" height="10" viewBox="0 0 14 14" fill="none"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
                    </button>
                  )}
                </div>

                <div className="relative">
                  <select className={selectClass} value={category} onChange={(e) => setCategory(e.target.value)}>
                    {allCategories.map((c) => <option key={c}>{c}</option>)}
                  </select>
                  <svg className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" width="10" height="10" viewBox="0 0 10 6" fill="none">
                    <path d="M1 1l4 4 4-4" stroke="#012E72" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </div>
                <div className="relative">
                  <select className={selectClass} value={tag} onChange={(e) => setTag(e.target.value)}>
                    {allTags.map((t) => <option key={t}>{t}</option>)}
                  </select>
                  <svg className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" width="10" height="10" viewBox="0 0 10 6" fill="none">
                    <path d="M1 1l4 4 4-4" stroke="#012E72" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </div>
                <div className="relative">
                  <select className={selectClass} value={sort} onChange={(e) => setSort(e.target.value)}>
                    {["Newest", "Oldest", "Popular", "A-Z"].map((s) => <option key={s}>{s}</option>)}
                  </select>
                  <svg className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" width="10" height="10" viewBox="0 0 10 6" fill="none">
                    <path d="M1 1l4 4 4-4" stroke="#012E72" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </div>
                <button onClick={handleReset}
                  className="font-cinzel font-bold text-[0.72rem] tracking-[0.15em] uppercase text-white-pure bg-blue-primary border border-blue-primary rounded-full px-5 py-2.5 hover:bg-white-pure hover:text-blue-dark transition-all duration-200">
                  Reset
                </button>
              </div>
            </div>

            {/* Right: laptop image — flush to bottom-right corner */}

          </div>
        </div>
      </div>

      {/* ══ CAROUSEL ══ */}
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-10">
        <div className="flex items-center justify-end gap-2 mb-4">
          <button onClick={() => scrollCarousel(-1)}
            className="w-9 h-9 rounded-full border border-blue-dark/20 bg-white-pure flex items-center justify-center hover:bg-blue-dark hover:text-white-pure hover:border-blue-dark transition-all duration-200 text-blue-dark">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M9 2L4 7l5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
          <button onClick={() => scrollCarousel(1)}
            className="w-9 h-9 rounded-full border border-blue-dark/20 bg-white-pure flex items-center justify-center hover:bg-blue-dark hover:text-white-pure hover:border-blue-dark transition-all duration-200 text-blue-dark">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M5 2l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
        </div>
        <div ref={carouselRef} className="flex gap-4 overflow-x-auto scrollbar-hide pb-2 scroll-smooth">
          {filtered.map((article) => (
            <CarouselCard key={article.title} article={article} onNavigate={onNavigate} />
          ))}
        </div>
      </div>

      {/* ══ BLOG GRID ══ */}
      <div className="max-w-7xl mx-auto px-6 sm:px-10 pb-16">
        <div className="bg-white-pure rounded-3xl border border-blue-dark/8 shadow-[0_2px_24px_rgba(1,46,114,0.06)] p-8">
          <h2 className="font-cinzel font-black text-blue-dark text-2xl tracking-[0.05em] uppercase mb-8">
            Blog Posts &amp; Articles
          </h2>

          {loadingDB ? (
            <div className="flex items-center justify-center py-20">
              <svg className="animate-spin" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="rgba(1,46,114,0.4)" strokeWidth="2">
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
            </div>
          ) : paginated.length === 0 ? (
            <div className="text-center py-20">
              <p className="font-constantia italic text-blue-dark/40 text-lg">No articles match your filters.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {paginated.map((article) => (
                <BlogCard key={article.title} article={article} onNavigate={onNavigate} />
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-10">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                className="w-9 h-9 rounded-full border border-blue-dark/20 flex items-center justify-center text-blue-dark disabled:opacity-30 hover:bg-blue-dark hover:text-white-pure hover:border-blue-dark transition-all duration-200">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M9 2L4 7l5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                <button key={n} onClick={() => setPage(n)}
                  className={`w-9 h-9 rounded-full font-cinzel font-bold text-sm transition-all duration-200
                    ${page === n ? "bg-blue-dark text-white-pure" : "border border-blue-dark/20 text-blue-dark hover:bg-blue-dark/8"}`}>
                  {n}
                </button>
              ))}
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="w-9 h-9 rounded-full border border-blue-dark/20 flex items-center justify-center text-blue-dark disabled:opacity-30 hover:bg-blue-dark hover:text-white-pure hover:border-blue-dark transition-all duration-200">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M5 2l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </button>
            </div>
          )}
        </div>
      </div>

      <style>{`.scrollbar-hide::-webkit-scrollbar{display:none}.scrollbar-hide{-ms-overflow-style:none;scrollbar-width:none}`}</style>

      <TestimonialSection />
      <Footer />
    </div>
  );
}